import { generatePath } from '@ps/web-core-utils';
import type {
  ChangePlayersStatusRequest,
  ChangePlayersStatusResponse,
  CloseClubRequest,
  CloseClubResponse
} from '@ps/web-shared-api-types/v0/SwHomeGames';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router';

import useRequest from '@swf/core/hooks/useRequest';
import type { Dispatch } from '@swf/core/store';
import { select } from '@swf/core/store';
import { handleServerResponse } from '@swf/poker';

import ENDPOINTS from '../../../../constants/endpoints';
import { useHomeGamesWidget } from '../../../../model';
import { ModalRoutes, UserClubRole } from '../../../../types/common';

const parseResponse = (
  body: CloseClubResponse | ChangePlayersStatusResponse,
  isManager: boolean
) => {
  if (isManager) {
    return handleServerResponse<CloseClubResponse, CloseClubRequest>({
      response: body,
      accessor: 'closeClubReply'
    });
  }

  return handleServerResponse<ChangePlayersStatusResponse, ChangePlayersStatusRequest>({
    response: body,
    accessor: 'changePlayersStatusReply'
  });
};

const useCloseOrLeaveClub = () => {
  const { homeGamesClubs = '' } = useSelector(select.cms.getAliases);
  const { widgetDispatch } = useHomeGamesWidget();
  const request = useRequest();
  const [loading, setLoading] = useState(false);
  const history = useHistory();
  const { search } = useLocation();
  const { modal } = useDispatch<Dispatch>();
  const { clubId } = useParams<{ clubId: string }>();
  const action = (userRole: UserClubRole) => {
    setLoading(true);

    const isManager = userRole === UserClubRole.Manager;

    request({
      service: `${isManager ? ENDPOINTS.club : ENDPOINTS.leaveClub}`,
      http: true,
      method: 'DELETE',
      body: { clubId: Number(clubId) }
    }).subscribe({
      next: ({ body }) => {
        const { error } = parseResponse(body, isManager);

        if (error) {
          modal.enqueue({
            path: ModalRoutes.notification,
            locationState: { text: error }
          });
        } else {
          widgetDispatch.fetchUserClubs();
          history.replace({
            pathname: generatePath(homeGamesClubs, {}),
            search
          });
        }

        setLoading(false);
      }
    });
  };

  return { action, loading };
};

export default useCloseOrLeaveClub;
